import { Card, Table, Text } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ProjectSummary } from '@/api/projects';
import { StatusBadge } from '@/components/StatusBadge';
import { CellText } from '@/components/CellText';
import { NAME_MAX_WIDTH, NAME_MIN_WIDTH } from '@/components/tableWidths';
import { rowClick, rowStyle } from '@/components/rowClick';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { InternalStateChip } from './InternalStateChip';

export interface ProjectsTableProps {
  projects: ProjectSummary[];
  /** On a page scoped to one organization the column would repeat its name on every row. */
  hideOrganization?: boolean;
}

/** What waits on staff in a project: analyses in review, ready to launch, or failed (UX review §2.1). */
export function waitingOnUs(p: ProjectSummary): number | null {
  const s = p.analyses_summary;
  if (s.review == null || s.ready == null || s.failed == null) return null;
  return s.review + s.ready + s.failed;
}

function Count({ n, tone }: { n: number | null | undefined; tone?: string }) {
  const { number } = useFormat();
  if (n == null) return <>—</>;
  if (n === 0) return <Text span fz={13} c={brand.textMuted}>0</Text>;
  return (
    <Text span fz={14} fw={700} c={tone}>
      {number(n)}
    </Text>
  );
}

/** Dense staff table over projects across organizations, sorted by what waits on staff. */
export function ProjectsTable({ projects, hideOrganization = false }: ProjectsTableProps) {
  const { t } = useTranslation('projects');
  const { date, dateTime, number, relative } = useFormat();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(projects, {
    organization: (p) => p.organization.name,
    name: (p) => p.name,
    state: (p) => p.state,
    status: (p) => t(`status.project.${p.client_status}`, { defaultValue: p.client_status, ...p.analyses_summary }),
    objects: (p) => p.objects_count,
    reports: (p) => p.reports_count,
    created: (p) => Date.parse(p.created_at),
    waiting: waitingOnUs,
    running: (p) => p.analyses_summary.running,
    failed: (p) => p.analyses_summary.failed,
    activity: (p) => (p.last_activity_at ? Date.parse(p.last_activity_at) : null),
  }, { key: 'waiting', direction: 'descending' });
  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            {!hideOrganization && <SortableTh {...sortProps('organization')}>{t('project.organization')}</SortableTh>}
            <SortableTh {...sortProps('name')}>{t('project.name')}</SortableTh>
            <SortableTh {...sortProps('state')}>{t('project.state')}</SortableTh>
            <SortableTh {...sortProps('status')}>{t('staff.clientStatus')}</SortableTh>
            <SortableTh {...sortProps('waiting')} ta="right">{t('staff.work.waiting')}</SortableTh>
            <SortableTh {...sortProps('running')} ta="right">{t('staff.work.running')}</SortableTh>
            <SortableTh {...sortProps('failed')} ta="right">{t('staff.work.failed')}</SortableTh>
            <SortableTh {...sortProps('activity')}>{t('staff.work.lastActivity')}</SortableTh>
            <SortableTh {...sortProps('objects')} ta="right">{t('staff.objectsCount')}</SortableTh>
            <SortableTh {...sortProps('reports')} ta="right">{t('staff.reportsCount')}</SortableTh>
            <SortableTh {...sortProps('created')}>{t('project.created')}</SortableTh>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((p) => (
            <Table.Tr
              key={p.public_id}
              style={rowStyle}
              onClick={rowClick(() => void navigate({ to: '/staff/projects/$projectId', params: { projectId: p.public_id } }))}
            >
              {!hideOrganization && (
                <Table.Td>
                  <CellText lines={2} fz={14} miw={110} maw={200}>
                    {p.organization.name}
                  </CellText>
                </Table.Td>
              )}
              <Table.Td>
                <CellText lines={2} fz={14} fw={600} miw={NAME_MIN_WIDTH} maw={NAME_MAX_WIDTH} title={p.name}>
                  <Link to="/staff/projects/$projectId" params={{ projectId: p.public_id }} style={{ color: brand.text, textDecoration: 'none' }}>
                    {p.name}
                  </Link>
                </CellText>
              </Table.Td>
              <Table.Td>
                <InternalStateChip state={p.state ?? 'active'} />
              </Table.Td>
              <Table.Td>
                <StatusBadge status={p.client_status} scope="project" counts={p.analyses_summary} />
              </Table.Td>
              <Table.Td ta="right" title={t('staff.work.waitingHint', { review: p.analyses_summary.review ?? 0, ready: p.analyses_summary.ready ?? 0, failed: p.analyses_summary.failed ?? 0 })}>
                <Count n={waitingOnUs(p)} />
              </Table.Td>
              <Table.Td ta="right">
                <Count n={p.analyses_summary.running} />
              </Table.Td>
              <Table.Td ta="right">
                <Count n={p.analyses_summary.failed} tone="red" />
              </Table.Td>
              <Table.Td title={p.last_activity_at ? dateTime(p.last_activity_at) : undefined}>
                <Text fz={13} c={brand.textMuted}>
                  {p.last_activity_at ? relative(p.last_activity_at) : '—'}
                </Text>
              </Table.Td>
              <Table.Td ta="right">{p.objects_count == null ? '—' : number(p.objects_count)}</Table.Td>
              <Table.Td ta="right">{p.reports_count == null ? '—' : number(p.reports_count)}</Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>{date(p.created_at)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
